
import {
  computed, defineComponent, watch,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import Button from 'primevue/button';
import Gleap from 'gleap';
import WizardDialog from '@/components/billing/shared/WizardDialog.vue';
import WizardBackButton from '@/components/billing/shared/WizardBackButton.vue';
import WizardActionButton from '@/components/billing/shared/WizardActionButton.vue';
import PlanComparator from '@/components/billing/dialogs/PlanComparator.vue';
import AddCreditsStep from '@/components/billing/dialogs/AddCreditsStep.vue';
import ReviewAndPayStep from '@/components/billing/dialogs/ReviewAndPayStep.vue';
import { useWizard } from '@/composables/billing/useWizard';

const PHASES = ['select', 'credits', 'review'] as const;

export default defineComponent({
  name: 'ChangePlanDialog',

  components: {
    Button,
    WizardDialog,
    WizardBackButton,
    WizardActionButton,
    PlanComparator,
    AddCreditsStep,
    ReviewAndPayStep,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    initialPlan: {
      type: String as () => string | null,
      default: null,
    },

    initialFrequency: {
      type: String as () => 'monthly' | 'yearly',
      default: 'monthly',
    },

    /**
     * Shop-level active discount percentage on the plan price (forwarded
     * to PlanComparator). When set, each plan card displays both the
     * struck-through catalog price AND the discounted price + a "-X%"
     * pill. Null / 0 ⇒ no discount UI on the comparator.
     */
    discountPercent: {
      type: Number as () => number | null,
      default: null,
    },
  },

  emits: ['update:visible', 'paid'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const wizard = useWizard(PHASES);

    const selectedPlan = ref<string | null>(props.initialPlan);
    const selectedFrequency = ref<'monthly' | 'yearly'>(props.initialFrequency);
    const selectedTier = ref<number | undefined>(undefined);
    const selectedSmsInclusion = ref<number>(0);
    const locked = ref(false);
    // Error code emitted by PlanComparator when the back returns a structured
    // 400 (e.g. PAGES_VIEWED_UNAVAILABLE for a V4 shop without page stats).
    // When set, the wizard replaces phase 1 with a "Contact support" block.
    const supportErrorCode = ref<string | null>(null);

    // V5 auto-tier-upgrade configuration. Active par défaut, plafond auto-
    // calculé par TierAutoUpgradeBlock (= tier choisi + 3 paliers, ou tier
    // max disponible). Forwardé à `subscribePlan` via ReviewAndPayStep.
    const tierAutoUpgradeEnabled = ref<boolean>(true);
    const tierAutoUpgradeMax = ref<number>(0);

    const onPlanComparatorError = (code: string) => {
      supportErrorCode.value = code;
    };

    const openSupport = () => {
      try {
        Gleap.open();
      } catch {
        // Gleap not initialised — fallback to a direct mailto.
        window.open(
          'mailto:contact@shopimind.com?subject=Demande%20de%20support%20-%20Changement%20d%27offre',
          '_blank',
        );
      }
    };

    const currency = computed<'EUR' | 'USD'>(
      () => (store.state.billing.overview?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );

    // Pre-fill source for `AddCreditsStep` — the shop's currently subscribed
    // monthly SMS amount. Read from the overview's `plan.currentSmsPlan` (set
    // by the back from `shops_plans.plan.smsPlan`). If null, the toggle opens
    // deactivated; if > 0, the toggle is on and the amount is pre-saisi.
    const currentSmsPlanAmount = computed<number>(() => (
      store.state.billing.overview?.plan?.currentSmsPlan?.amount ?? 0
    ));

    const titles = computed(() => ({
      select: t('billing.change.titleSelect'),
      credits: t('billing.change.titleCredits'),
      review: t('billing.change.titleReview'),
    }));

    const subtitles = computed(() => ({
      select: t('billing.change.subtitleSelect'),
      credits: t('billing.change.subtitleCredits'),
      review: t('billing.change.subtitleReview'),
    }));

    const reset = () => {
      wizard.reset();
      selectedPlan.value = props.initialPlan;
      selectedFrequency.value = props.initialFrequency;
      selectedTier.value = undefined;
      selectedSmsInclusion.value = 0;
      locked.value = false;
      supportErrorCode.value = null;
      tierAutoUpgradeEnabled.value = true;
      tierAutoUpgradeMax.value = 0;
    };

    const onVisibleChange = (v: boolean) => {
      if (!v) onClose();
    };

    const onClose = () => {
      if (locked.value) return;
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onPaid = () => {
      // For upgrades, `handleReload` (inside ReviewAndPayStep) reloads the
      // page on `requiresAuthRefresh=true` — the modal disappears as a
      // side-effect of the navigation. Downgrades return `completed` WITHOUT
      // `requiresAuthRefresh` (no payment was made) — without an explicit
      // close here the modal would linger on top of the freshly-refreshed
      // overview.
      locked.value = true;
      emit('paid');
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onShopifyRedirect = () => {
      locked.value = true;
    };

    watch(() => props.visible, (v) => { if (v) reset(); });

    return {
      t,
      PHASES,
      wizard,
      titles,
      subtitles,
      selectedPlan,
      selectedFrequency,
      selectedTier,
      selectedSmsInclusion,
      currency,
      currentSmsPlanAmount,
      tierAutoUpgradeEnabled,
      tierAutoUpgradeMax,
      locked,
      supportErrorCode,
      onPlanComparatorError,
      openSupport,
      onVisibleChange,
      onClose,
      onPaid,
      onShopifyRedirect,
    };
  },
});
